import { useEffect, useState } from "react";
import Input from "../../../form/Input";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
// import { bannerAddEdit } from "../../../../services/bannersService";
import FileUploadSingle from "../../../form/FileUploadSingle";
import Textarea from "../../../form/Textarea";
import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { collectionDetails } from "../../../../services/collectionService";
import AvatarUpload from "../../../common/AvatarUpload";
import { initialsValue } from "../../../../helpers";
import Tabs from "../../../elements/Tabs";
import { eventAddEdit, eventDetails } from "../../../../services/eventService";
import DatePicker from "../../../form/DatePicker";

const validation = {
  title: { required: true, message: "Title is required" },
};

function FormComponent({
  getResponse = () => {},
  close = () => {},
  data,
  lang,
}) {
  const queryClient = useQueryClient();
  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
  } = useForm({
    validationRules: validation,
    initialState: { gallery: [""] },
  });

  const { data: details } = useQuery({
    queryKey: ["details", data?._id],
    queryFn: async () => {
      const res = await eventDetails({ id: data?._id });
      return res;
    },
    enabled: !!data?._id,
  });

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ["details", data?._id] });
    };
  }, []);

  useEffect(() => {
    if (details?.data?._id) {
      if (details?.status === 200) {
        const value = {
          ...details?.data,
          title: details?.data?.title?.[lang] || "",
          description: details?.data?.description?.[lang] || "",
          gallery: details?.data?.gallery?.map((item) => item?.url),
        };
        delete value?._id;
        delete value?.createdAt;
        setFieldsValue(value);
      }
    }
  }, [details?.data?._id, lang]);

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (val) => {
      const payload = {
        ...val,
        title: { ...details?.data?.title, [lang]: val?.title },
        description: {
          ...details?.data?.description,
          [lang]: val?.description,
        },
        organization: data?.organization,
        ...(data?._id && { id: data?._id }),
      };
      return await eventAddEdit(payload);
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        toast.success(res?.message);
        getResponse(res);
        return res;
      } else {
        toast.warning(res?.message || res?.error);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return (
    <form onSubmit={(event) => handleSubmit(event, mutate)}>
      <div className="grid grid-cols-12 gap-x-4 gap-y-4">
        <div className="col-span-12 px-6 flex items-center justify-center relative">
          <AvatarUpload
            src={
              values?.logo instanceof File
                ? URL.createObjectURL(values?.logo)
                : values?.logo
            }
            onChange={(file) => {
              handleChange({ target: { name: "logo", value: file } });
            }}
            alt="profile"
            initial={initialsValue(details?.name)}
          />
        </div>
        <div className="col-span-12">
          <Input
            label="Event Name"
            inputPlaceholder={"Enter Event Name"}
            inputName="title"
            value={values?.title || ""}
            onChange={handleChange}
            {...formValidate(errors, "title")}
          />
        </div>
        <div className="col-span-12">
          <Textarea
            label="Event Description"
            inputPlaceholder={"Enter Event Description"}
            inputName="description"
            inputValue={values?.description || ""}
            onChange={handleChange}
            {...formValidate(errors, "description")}
          />
        </div>
        <div className="col-span-6">
          <DatePicker
            label={"Event start date"}
            placeholder={"Select start date"}
            value={{ startDate: values?.startDate, endDate: values?.startDate }}
            onChange={({ startDate }) =>
              handleChange({ target: { name: "startDate", value: startDate } })
            }
          />
        </div>
        <div className="col-span-6">
          <DatePicker
            label={"Event end date"}
            value={{ startDate: values?.endDate, endDate: values?.endDate }}
            placeholder={"Select end date"}
            onChange={({ startDate }) =>
              handleChange({ target: { name: "endDate", value: startDate } })
            }
          />
        </div>

        <div className="col-span-12 space-y-3">
          <div className="text-xs font-medium text-text-disabled mb-1 font-CanelaDeck">
            Add Gallery
          </div>
          {values?.gallery?.map((image, i) => (
            <div
              key={i?.toString()}
              className="grid grid-cols-12 gap-4 bg-slate-50 rounded-lg p-4"
            >
              <div className="relative col-span-10">
                <FileUploadSingle
                  label={"Upload Image"}
                  labelClasses={"!mb-2"}
                  value={image || ""}
                  onChange={(file) => {
                    setFieldsValue({
                      gallery: values?.gallery?.map((item, index) =>
                        index === i ? file : item
                      ),
                    });
                  }}
                  {...formValidate(errors, "coverImage")}
                />
              </div>
              <div className="relative col-span-2 flex items-center justify-center h-full">
                <Button
                  buttonType="button"
                  buttonFunction={() => {
                    const updatedGallery = values.gallery.filter(
                      (_, index) => index !== i
                    );
                    setFieldsValue({ gallery: updatedGallery });
                  }}
                  buttonIcon="fa-regular fa-trash"
                  buttonClasses="!p-2 !h-auto !w-auto !border-none"
                  isDisable={isLoading}
                />
              </div>
            </div>
          ))}
          <div>
            <Button
              buttonLabel={"Add more"}
              variant="secondary"
              buttonFunction={() => {
                setFieldsValue({
                  gallery: [...(values?.gallery || []), ""],
                });
              }}
            />
          </div>
        </div>
      </div>
      <div className="relative col-span-4 mt-5">
        <div className="flex items-center justify-center gap-4">
          <Button
            buttonType="submit"
            buttonLabel={"Save Event"}
            variant="secondary"
            {...(isLoading && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
          <Button
            buttonLabel={"Cancel"}
            variant="primary"
            buttonFunction={close}
          />
        </div>
      </div>
    </form>
  );
}

const ManageEvent = ({ getResponse = () => {}, close = () => {}, data }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const tabs = [
    {
      label: "English",
      content: (
        <FormComponent
          getResponse={getResponse}
          close={close}
          data={data}
          lang={"en"}
        />
      ),
    },
    {
      label: "Spanish",
      content: (
        <FormComponent
          getResponse={getResponse}
          close={close}
          data={data}
          lang={"es"}
        />
      ),
    },
  ];

  return (
    <div className="h-full p-4">
      <Tabs
        items={tabs}
        setSelectedIndex={setSelectedIndex}
        selectedIndex={selectedIndex}
      />
    </div>
  );
};

export default ManageEvent;
